import { Link } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";

import { StoryCard } from "@/components/StoryCard";
import { VerticalVideoFrame } from "@/components/VerticalVideoFrame";
import { usePageSeo } from "@/hooks/usePageSeo";
import { Button } from "@/components/ui/button";
import { getStorySection } from "@/utils/storySections";

const SECTION_CONFIG = {
  sports: {
    overline: "Sports desk",
    title: "Sports Events",
    description: "Houston sports coverage, game-day reporting, highlights, and local athletic updates from the newsroom.",
  },
  editorial: {
    overline: "What’s new desk",
    title: "What’s New",
    description: "Fresh updates, features, investigations, and wider newsroom reporting that does not belong in breaking news or sports.",
  },
};

export const SectionFeedPage = ({ feed, section }) => {
  const config = SECTION_CONFIG[section];
  const stories = feed.filter((story) => getStorySection(story) === section);
  const featuredStory = stories[0];

  usePageSeo({
    canonicalPath: section === "sports" ? "/sports" : "/whats-new",
    description: section === "sports"
      ? "Get Houston sports news, game-day coverage, high school highlights, and local sports updates from Content Watch HQ."
      : "See what’s new across Houston with fresh updates, investigations, feature stories, and office-published newsroom coverage from Content Watch HQ.",
    title: section === "sports" ? "Houston Sports News | Content Watch HQ" : "What’s New | Content Watch HQ",
  });

  return (
    <main className="page-shell mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8" data-testid={`${section}-page`}>
      <section className="grid gap-8 border border-[rgba(10,15,26,0.12)] bg-white p-6 lg:grid-cols-[minmax(0,1fr)_320px] lg:p-8" data-testid={`${section}-hero`}>
        <div>
          <p className="eyebrow text-red-600" data-testid={`${section}-overline`}>{config.overline}</p>
          <h1 className="headline-lg mt-3" data-testid={`${section}-title`}>{config.title}</h1>
          <p className="mt-4 max-w-2xl text-base leading-8 text-slate-700" data-testid={`${section}-description`}>{config.description}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button asChild className="rounded-none bg-[#D21404] px-5 py-5 text-xs font-bold uppercase tracking-[0.2em] text-white hover:bg-[#B31203]" data-testid={`${section}-breaking-link-button`}>
              <Link to="/breaking-news">
                Back to Breaking News
                <ArrowRight size={16} weight="bold" />
              </Link>
            </Button>
          </div>
        </div>
        {featuredStory ? (
          <VerticalVideoFrame compact story={featuredStory} />
        ) : (
          <div className="border border-[rgba(10,15,26,0.12)] bg-slate-50 p-6" data-testid={`${section}-hero-empty-state`}>
            <p className="eyebrow text-red-600">Waiting for uploads</p>
            <p className="mt-3 text-sm leading-7 text-slate-700">Once your newsroom publishes content for this section, it will appear here automatically.</p>
          </div>
        )}
      </section>

      <section className="mt-8" data-testid={`${section}-stories-grid`}>
        {stories.length ? (
          <div className="news-card-grid">
            {stories.map((story) => (
              <StoryCard key={story.id} story={story} />
            ))}
          </div>
        ) : (
          <div className="border border-[rgba(10,15,26,0.12)] bg-white p-8" data-testid={`${section}-empty-state`}>
            <p className="eyebrow text-red-600">No stories yet</p>
            <p className="mt-3 text-base leading-8 text-slate-700">Once the newsroom publishes content for this desk, it will appear here automatically.</p>
          </div>
        )}
      </section>
    </main>
  );
};